import { ServiceType, SlotMode } from "../DTOs/PlannedSermon";

/**
 * The fields of a service that come from the sermon plan and may be updated by
 * a re-import. Everything else on the service – topic, Bible passage, prayer
 * concerns, confirmations, choir songs – was entered by preachers or the
 * conductor and is never touched by an import.
 */
export interface PlanFields {
    sermon1PreacherKey: string;
    sermon2PreacherKey: string;
    sermon1Mode: SlotMode;
    sermon2Mode: SlotMode;
    serviceLabel: string;
}

export const PLAN_FIELDS: readonly (keyof PlanFields)[] = [
    "sermon1PreacherKey",
    "sermon2PreacherKey",
    "sermon1Mode",
    "sermon2Mode",
    "serviceLabel",
];

/** Document id of the service a plan row is stored as (one per date and service type). */
export function plannedServiceDocId(date: string, serviceType: ServiceType | undefined): string {
    const serviceTypeKey = (serviceType || "manual").replace(/[^a-z0-9-]/gi, "_").toLowerCase();
    return `service_${date}_${serviceTypeKey}`;
}

/** Reads the plan fields from a stored service document, applying the defaults of the converter. */
export function planFieldsOf(data: Record<string, unknown>): PlanFields {
    const text = (value: unknown) => (typeof value === "string" ? value : "");
    return {
        sermon1PreacherKey: text(data.sermon1PreacherKey),
        sermon2PreacherKey: text(data.sermon2PreacherKey),
        sermon1Mode: (text(data.sermon1Mode) || "sermon") as SlotMode,
        sermon2Mode: (text(data.sermon2Mode) || "sermon") as SlotMode,
        serviceLabel: text(data.serviceLabel),
    };
}

/** The plan fields whose value in the import differs from the stored service (empty = unchanged). */
export function diffPlanFields(existing: PlanFields, incoming: PlanFields): Partial<PlanFields> {
    const changes: Partial<PlanFields> = {};
    for (const field of PLAN_FIELDS) {
        if (existing[field] !== incoming[field]) {
            (changes as Record<string, string>)[field] = incoming[field];
        }
    }
    return changes;
}
